import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  CheckCircle, Circle, Hourglass, CompassTool, Calendar, CurrencyInr,
  Sparkle, Buildings, Flag, ChatCircleDots,
} from "@phosphor-icons/react";
import ClientRequestForm from "../components/ClientRequestForm";
import ProjectDeliverables from "../components/ProjectDeliverables";
import MilestoneApproveButton from "../components/MilestoneApproveButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusMeta = {
  todo: { label: "Not started", icon: Circle, color: "text-zinc-400 border-zinc-300", dot: "bg-zinc-300" },
  in_progress: { label: "In progress", icon: Hourglass, color: "text-blue-700 border-blue-600", dot: "bg-blue-600" },
  complete: { label: "Complete", icon: CheckCircle, color: "text-emerald-700 border-emerald-600", dot: "bg-emerald-600" },
};

const PROGRESS_INITIAL = { width: 0 };
const PROGRESS_TRANSITION = { duration: 0.8, ease: "easeOut" };

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function money(n) {
  if (!n && n !== 0) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);
}

export default function ClientView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/public/projects/${token}`);
      setData(r.data);
    } catch {
      setError("This link is invalid or has expired.");
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-zinc-50">
        <div className="border-2 border-zinc-900 shadow-brutal bg-white max-w-md p-8 text-center">
          <div className="label-overline text-red-600 mb-2">Access Denied</div>
          <h1 className="font-display text-2xl font-black tracking-tighter uppercase text-zinc-900">
            Link Not Found
          </h1>
          <p className="mt-3 text-sm text-zinc-600">
            The share link is invalid, revoked, or the project has been removed.
            Please contact your project architect.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="label-overline text-zinc-500 ants px-6 py-4">Loading Project…</div>
      </div>
    );
  }

  const { project, progress, billing, tasks } = data;
  const milestones = tasks.filter((t) => t.is_milestone);
  const otherTasks = tasks.filter((t) => !t.is_milestone);

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="client-view">
      {/* Header */}
      <header className="border-b border-zinc-300 bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-9 h-9 border-2 border-orange-500 flex items-center justify-center">
              <CompassTool size={18} weight="bold" className="text-orange-500" />
            </div>
            <span className="label-overline text-orange-500">Dream Home Planners</span>
            <span className="label-overline text-zinc-500 ml-auto">Client Portal</span>
          </div>
          <div className="label-overline text-blue-400 mb-2 flex items-center gap-2">
            <Buildings size={12} weight="bold" /> Project
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase leading-none">
            {project}
          </h1>
          <p className="mt-4 text-zinc-400 max-w-2xl text-sm leading-relaxed">
            Live progress on your project. Updated automatically as our team completes each phase.
          </p>
        </div>
      </header>

      {/* Progress + Billing summary */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-300 bg-white">
          {/* Progress */}
          <div className="p-6 border-b md:border-b-0 md:border-r border-zinc-300" data-testid="progress-block">
            <div className="flex items-center justify-between">
              <div className="label-overline text-zinc-500">Overall Progress</div>
              <div className="label-overline text-blue-600">{progress.pct}%</div>
            </div>
            <div className="font-display text-5xl font-black tracking-tighter text-zinc-900 mt-3">
              {progress.complete}<span className="text-zinc-400">/{progress.total}</span>
            </div>
            <div className="label-overline text-zinc-500 mt-1">Tasks Completed</div>

            <div className="mt-6 h-2 bg-zinc-200 relative overflow-hidden">
              <motion.div
                initial={PROGRESS_INITIAL}
                animate={{ width: `${progress.pct}%` }}
                transition={PROGRESS_TRANSITION}
                className="absolute inset-y-0 left-0 bg-blue-600"
              />
            </div>
            <div className="flex items-center justify-between mt-3 text-xs text-zinc-500 font-mono">
              <span>{progress.in_progress} active</span>
              <span>{progress.total - progress.complete - progress.in_progress} pending</span>
            </div>
          </div>

          {/* Billing */}
          <div className="p-6" data-testid="billing-block">
            <div className="flex items-center justify-between">
              <div className="label-overline text-zinc-500 flex items-center gap-1.5">
                <CurrencyInr size={12} weight="bold" /> Billing Milestones
              </div>
              <div className="label-overline text-orange-600">
                {billing.milestone_complete}/{billing.milestone_count} DONE
              </div>
            </div>
            <div className="font-display text-4xl font-black tracking-tighter text-zinc-900 mt-3 break-all">
              {money(billing.billed_amount)}
            </div>
            <div className="label-overline text-zinc-500 mt-1">Invoiced To Date</div>

            <div className="mt-6 grid grid-cols-2 gap-0 border border-zinc-300">
              <div className="p-3 border-r border-zinc-300">
                <div className="label-overline text-zinc-500">Contract Value</div>
                <div className="font-display font-bold text-zinc-900 mt-1">{money(billing.total_value)}</div>
              </div>
              <div className="p-3">
                <div className="label-overline text-zinc-500">Remaining</div>
                <div className="font-display font-bold text-orange-700 mt-1">{money(billing.remaining_amount)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Billing Milestones Timeline */}
        {milestones.length > 0 && (
          <div className="mt-12" data-testid="milestones-section">
            <div className="flex items-center gap-2 mb-6">
              <Flag size={16} weight="bold" className="text-orange-600" />
              <span className="label-overline text-zinc-900">Billing Milestones</span>
              <div className="flex-1 h-px bg-zinc-300 ml-2" />
            </div>
            <div className="border border-zinc-300 bg-white">
              {milestones.map((m, i) => {
                const meta = statusMeta[m.status] || statusMeta.todo;
                const Icon = meta.icon;
                return (
                  <div key={m.id} data-testid={`milestone-${m.id}`}
                       className={`p-5 ${i < milestones.length - 1 ? "border-b border-zinc-200" : ""}`}>
                    <div className="grid grid-cols-[40px_1fr_auto] gap-4 items-center">
                      <div className="flex justify-center">
                        <div className={`w-10 h-10 border-2 flex items-center justify-center ${meta.color}`}>
                          <Icon size={18} weight="bold" />
                        </div>
                      </div>
                      <div className="min-w-0">
                        <div className="font-display font-bold text-zinc-900 truncate">{m.title}</div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 font-mono flex-wrap">
                          <span className={`label-overline ${meta.color.split(" ")[0]}`}>{meta.label}</span>
                          {m.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={11} weight="bold" /> {formatDate(m.due_date)}
                            </span>
                          )}
                          {m.request_count > 0 && (
                            <span className="flex items-center gap-1 text-blue-700" title="Requests you've sent">
                              <ChatCircleDots size={11} weight="bold" /> {m.request_count} request{m.request_count > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={`font-display text-xl font-black tracking-tighter ${
                        m.status === "complete" ? "text-emerald-700" : "text-zinc-900"
                      }`}>
                        {money(m.milestone_amount)}
                      </div>
                    </div>
                    <ClientRequestForm
                      token={token}
                      taskId={m.id}
                      taskTitle={m.title}
                      onSubmitted={load}
                    />
                    <MilestoneApproveButton task={m} mode="public" shareToken={token} onApproved={load} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* All Tasks / Phases */}
        {otherTasks.length > 0 && (
          <div className="mt-12" data-testid="tasks-section">
            <div className="flex items-center gap-2 mb-6">
              <Sparkle size={16} weight="bold" className="text-blue-600" />
              <span className="label-overline text-zinc-900">Work Phases</span>
              <div className="flex-1 h-px bg-zinc-300 ml-2" />
            </div>
            <div className="border border-zinc-300 bg-white">
              {otherTasks.map((t, i) => {
                const meta = statusMeta[t.status] || statusMeta.todo;
                const Icon = meta.icon;
                return (
                  <div key={t.id} data-testid={`task-${t.id}`}
                       className={`p-4 ${i < otherTasks.length - 1 ? "border-b border-zinc-200" : ""}`}>
                    <div className="flex items-start gap-3">
                      <div className={`shrink-0 w-8 h-8 border flex items-center justify-center ${meta.color}`}>
                        <Icon size={14} weight="bold" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-zinc-900 break-words">{t.title}</div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500 font-mono flex-wrap">
                          <span className={`label-overline ${meta.color.split(" ")[0]}`}>{meta.label}</span>
                          {t.due_date && (
                            <span className="flex items-center gap-1">
                              <Calendar size={11} weight="bold" /> {formatDate(t.due_date)}
                            </span>
                          )}
                          {t.request_count > 0 && (
                            <span className="flex items-center gap-1 text-blue-700">
                              <ChatCircleDots size={11} weight="bold" /> {t.request_count} request{t.request_count > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <ClientRequestForm
                      token={token}
                      taskId={t.id}
                      taskTitle={t.title}
                      onSubmitted={load}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {tasks.length === 0 && (
          <div className="mt-12 border border-dashed border-zinc-400 p-12 text-center">
            <div className="label-overline text-zinc-400">No tasks scheduled yet</div>
          </div>
        )}

        {/* Deliverables (public download via share token) */}
        {(data.deliverables?.length > 0) && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <Sparkle size={16} weight="bold" className="text-orange-600" />
              <span className="label-overline text-zinc-900">Uploaded Deliverables</span>
              <div className="flex-1 h-px bg-zinc-300 ml-2" />
            </div>
            <ProjectDeliverables
              projectName={data.project}
              canUpload={false}
              canDelete={false}
              publicToken={token}
              forceLoad={data.deliverables}
              paymentGate={(data.fee_summary?.overdue_invoice_count || 0) > 0}
            />
          </div>
        )}

        {/* Requests & Replies thread */}
        {(data.requests?.length > 0) && (
          <div className="mt-12">
            <div className="flex items-center gap-2 mb-6">
              <ChatCircleDots size={16} weight="bold" className="text-purple-600" />
              <span className="label-overline text-zinc-900">Your Requests & Studio Replies</span>
              <div className="flex-1 h-px bg-zinc-300 ml-2" />
            </div>
            <div className="border border-zinc-300 bg-white">
              {data.requests.map((r, i) => (
                <div key={r.id} data-testid={`public-request-${r.id}`}
                     className={`p-5 ${i < data.requests.length - 1 ? "border-b border-zinc-200" : ""}`}>
                  <div className="label-overline text-zinc-500 mb-2">
                    <Buildings size={11} weight="bold" className="inline mr-1" />
                    {r.task_title || "Project"}
                  </div>
                  <div className="border-l-4 border-zinc-400 bg-zinc-50 px-4 py-3">
                    <div className="text-xs text-zinc-500 font-mono">
                      <span className="label-overline">{r.client_name}</span>
                      <span> · {new Date(r.created_at).toLocaleString()}</span>
                    </div>
                    <div className="mt-1.5 text-sm text-zinc-800 whitespace-pre-wrap">{r.message}</div>
                  </div>
                  {r.reply ? (
                    <div className="mt-3 border-l-4 border-blue-600 bg-blue-50 px-4 py-3"
                         data-testid={`public-reply-${r.id}`}>
                      <div className="label-overline text-blue-700">
                        <ChatCircleDots size={11} weight="bold" className="inline mr-1" />
                        Studio Reply
                        {r.replied_by && <span className="text-blue-800 normal-case tracking-normal"> · {r.replied_by}</span>}
                      </div>
                      <div className="mt-1.5 text-sm text-zinc-900 whitespace-pre-wrap">{r.reply}</div>
                      {r.replied_at && (
                        <div className="mt-1.5 text-[10px] text-zinc-500 font-mono">
                          {new Date(r.replied_at).toLocaleString()}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="mt-3 text-xs text-zinc-500 font-mono flex items-center gap-1">
                      <Hourglass size={11} weight="bold" /> Awaiting studio reply…
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <footer className="mt-16 pt-6 border-t border-zinc-300 flex items-center justify-between text-xs text-zinc-500 font-mono">
          <span>© Dream Home Planners · Client Portal</span>
          <span>Updated {formatDate(data.generated_at)}</span>
        </footer>
      </section>
    </div>
  );
}
